import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import {
    IShipDateConfigResponse,
    ShipDateMode,
    shipDateConfigAtom,
    shipDateConfigLoadingAtom
} from "./shipping_station_config_types";

/**
 * The order-handler returns `{ message, status }` on every handled failure — a 400
 * from the ship date validator is the actionable one here ("more than 7 days ahead",
 * "cannot be in the past"). The shared errorCatcher discards it, so pull it out.
 */
const serverMessage = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    if (typeof data?.message === 'string' && data.message.length > 0) return data.message;
    return fallback;
};

/**
 * Bumped on every write. `get` is polled on a timer and re-read after every scan, so a
 * read that was already in flight when a write happened comes back with the pre-change
 * date and would revert the banner. Module-scoped rather than a ref because the read and
 * the write are issued from different components, each with its own hook instance.
 */
let writeCount = 0;

type UseShipDateConfig = [
    IShipDateConfigResponse | null,
    {
        get: () => Promise<IShipDateConfigResponse | null>;
        set: (mode: ShipDateMode, overrideDate?: string) => Promise<IShipDateConfigResponse | null>;
    }
];

// #region useShipDateConfig
export const useShipDateConfig = (): UseShipDateConfig => {
    const [config, setConfig] = useAtom(shipDateConfigAtom);
    const setLoading = useSetAtom(shipDateConfigLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [employee, authToken, loginFunctions] = useLogin();

    const headers = { 'X-Wallmates-Auth': authToken };

    // #region get
    const get = async (): Promise<IShipDateConfigResponse | null> => {
        const writesAtStart = writeCount;
        try {
            setLoading(true);
            const response = await axios.get(`${environment.shippingUrl}/ship-date-config`, { headers });
            // A write landed while this read was in flight, so the read is already stale.
            if (writeCount === writesAtStart) setConfig(response.data);
            return response.data as IShipDateConfigResponse;
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            // No snackbar: this is polled on a timer and after every scan, so a
            // transient failure would spam the operator. The banner keeps the last
            // known value, which is still the right answer in AUTO mode.
            return null;
        } finally {
            setLoading(false);
        }
    };

    // #region set
    const set = async (mode: ShipDateMode, overrideDate?: string): Promise<IShipDateConfigResponse | null> => {
        writeCount++;
        try {
            const response = await axios.post(
                `${environment.shippingUrl}/ship-date-config`,
                { mode, overrideDate, employee: employee?.id ?? null },
                { headers }
            );
            setConfig(response.data);
            setSnackbar({
                show: true,
                snackbarLevel: 'success',
                text: mode === 'OVERRIDE'
                    ? `Ship date set to ${(response.data as IShipDateConfigResponse).resolved.shipDate}`
                    : 'Ship date back to automatic'
            });
            return response.data as IShipDateConfigResponse;
        } catch (e: any) {
            errorCatcher(e, loginFunctions.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: serverMessage(e, 'Failed to update the ship date.')
            });
            return null;
        }
    };

    return [config, { get, set }];
};
